import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Tabs, Collapse, Grid } from 'antd'
import type { TabsProps } from 'antd'
import styled, { css } from 'styled-components'
import { HrTimeLine } from '../hr-timeline/hr-timeline'
import { setStagePlanClear } from '../../../../../store/stagePlan/actions'
import { HrFilesTimeline } from '../hr-files-timeline/hr-files-timeline'
import { HrStagePlan } from '../hr-stage-plan/hr-stage-plan'
import { HrChats } from '../hr-chats/hr-chats'
import { setTimelineClear } from '../../../../../store/timeline/actions'
import { getTimeLineData, getTimeLineFiles } from '../../../../../store/timeline/action-creators'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { HrFormContent } from '../hr-forms/hr-form-content'
import { StyledCollapse } from '../../../../../components/v2/field-group/styles'
import { TimelineTab } from '../../styles'

const StyledTabs = styled(Tabs)<{ $isChatsActive: boolean }>`
  ${({ $isChatsActive }) =>
    $isChatsActive &&
    css`
      .ant-tabs-ink-bar {
        background-color: #007909;
      }
    `}
`

interface HrTabsProps {
  className?: string
  onChange?: (key: string) => void
  onTabClick?: (key: string) => void
  id: string | number
}

export const HrTabs: React.FC<HrTabsProps> = memo((props: HrTabsProps) => {
  const { className, onChange, onTabClick, id } = props
  const dispatch = useAppDispatch()
  const [activeKey, setActiveKey] = useState<string>('data')
  const [timelineLoaded, setTimelineLoaded] = useState<boolean>(false)
  const [fullTimelineLoaded, setFullTimelineLoaded] = useState<boolean>(false)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens?.md

  useEffect(() => {
    dispatch(setStagePlanClear())
    return () => {
      dispatch(setTimelineClear())
      dispatch(setStagePlanClear())
    }
  }, [dispatch])

  const handleOnChange = useCallback(
    key => {
      setActiveKey(key)
      if (id !== 'new') {
        if (key === 'timeline' && !fullTimelineLoaded) {
          dispatch(getTimeLineData('hr', id))
          setTimelineLoaded(true)
          setFullTimelineLoaded(true)
        } else if (key === 'files' && !timelineLoaded) {
          dispatch(getTimeLineFiles('hr', id))
          setTimelineLoaded(true)
        }
      }
      if (onChange) {
        onChange(key)
      }
    },
    [dispatch, id, timelineLoaded, fullTimelineLoaded, onChange],
  )

  const handleOnTabClick = useCallback(
    key => {
      if (onTabClick) {
        onTabClick(key)
      }
    },
    [onTabClick],
  )

  const tabItems = useMemo<TabsProps['items']>(() => {
    const items: TabsProps['items'] = [
      {
        key: 'data',
        label: 'Данные кандидата',
        children: <HrFormContent id={id} />,
      },
      {
        key: 'timeline',
        label: 'Лента событий',
        children: isMobile ? (
          <>
            <StyledCollapse ghost>
              <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
                <HrStagePlan id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
            <StyledCollapse defaultActiveKey={['timeline-panel']} ghost>
              <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
                <HrTimeLine id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
          </>
        ) : (
          <TimelineTab>
            <HrStagePlan id={id} />
            <HrTimeLine id={id} />
          </TimelineTab>
        ),
      },
    ]

    if (!isMobile) {
      items.push({
        key: 'chats',
        label: <span style={{ color: '#007909' }}>Чаты</span>,
        children: <HrChats id={id} />,
      })
    }

    items.push({
      key: 'files',
      label: 'Прикрепленные файлы',
      children: <HrFilesTimeline id={id} />,
    })

    return items
  }, [id, isMobile])

  return (
    <StyledTabs
      onChange={handleOnChange}
      onTabClick={handleOnTabClick}
      activeKey={activeKey}
      items={tabItems}
      $isChatsActive={activeKey === 'chats'}
      className={className}
    />
  )
})

export default HrTabs
